import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ShieldCheck, BellRing, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { roleLabel, fmtDate } from "@/lib/format";
import { subscribeToPush } from "@/lib/push";
import { PageHero } from "@/components/premium/UI";

const ROLES = [
  "administrator", "poultry_supervisor", "health_caregiver",
  "construction_supervisor", "finance_officer", "inventory_officer", "guest",
];

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [patient, setPatient] = useState(null);
  async function refresh() {
    try {
      const [u, p] = await Promise.all([api.get("/users"), api.get("/health/patients")]);
      setUsers(u.data);
      setPatient(p.data?.[0] || null);
    } catch { toast.error("Could not load settings"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHero
        eyebrow="Configuration"
        title="Settings"
        subtitle="User management, health thresholds, notification channels and integrations."
        moduleKind="settings"
      />

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-deepgreen" />Users & roles</CardTitle><CardDescription>{users.length} accounts</CardDescription></div>
          <NewUser onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>Active</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <UserRow key={u.id} u={u} onChanged={refresh} />
              ))}
              {users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center subtle py-8">No users yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {patient && <ThresholdCard patient={patient} onSaved={refresh} />}

      <NotificationsCard />
      <WhatsAppIntegrationCard />
    </div>
  );
}

function WhatsAppIntegrationCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [test, setTest] = useState("");
  useEffect(() => { api.get("/integrations/whatsapp").then((r) => { setCfg(r.data); setForm({ ...r.data, access_token: "", verification_token: "" }); }); }, []);
  async function save() {
    try {
      const patch = { ...form };
      if (!patch.access_token) delete patch.access_token;
      if (!patch.verification_token) delete patch.verification_token;
      const { data } = await api.patch("/integrations/whatsapp", patch);
      setCfg(data); toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function runTest() {
    if (!test) { toast.error("Enter a WhatsApp number to test"); return; }
    try { const { data } = await api.post("/integrations/whatsapp/test", { to_number: test }); toast[data.status === "sent" ? "success" : "warning"](`${data.status}: ${data.detail}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  if (!cfg) return null;
  return (
    <Card className="rounded-2xl" data-testid="whatsapp-card">
      <CardHeader>
        <CardTitle>WhatsApp Business integration</CardTitle>
        <CardDescription>Meta Cloud API. Credentials are encrypted at rest.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Business number</Label><Input value={form.business_number || ""} onChange={(e) => setForm({ ...form, business_number: e.target.value })} /></div>
          <div><Label>Phone number ID</Label><Input value={form.phone_number_id || ""} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} data-testid="wa-phone-id" /></div>
          <div><Label>Access token</Label><Input type="password" placeholder={cfg.access_token || "paste new token"} value={form.access_token || ""} onChange={(e) => setForm({ ...form, access_token: e.target.value })} data-testid="wa-access-token" /></div>
          <div><Label>Verification token</Label><Input type="password" placeholder={cfg.verification_token || "paste new token"} value={form.verification_token || ""} onChange={(e) => setForm({ ...form, verification_token: e.target.value })} /></div>
          <div><Label>Webhook URL</Label><Input value={form.webhook_url || ""} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} /></div>
          <div><Label>Default template</Label><Input value={form.default_template || ""} onChange={(e) => setForm({ ...form, default_template: e.target.value })} /></div>
          <div className="col-span-2 flex items-center gap-3">
            <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="wa-enabled" /> Enabled</label>
            <label className="text-sm flex items-center gap-2"><input type="radio" name="wa-mode" checked={form.mode === "sandbox"} onChange={() => setForm({ ...form, mode: "sandbox" })} /> Sandbox</label>
            <label className="text-sm flex items-center gap-2"><input type="radio" name="wa-mode" checked={form.mode === "production"} onChange={() => setForm({ ...form, mode: "production" })} /> Production</label>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} className="bg-deepgreen hover:bg-deepgreen/90" data-testid="wa-save">Save</Button>
          <Input placeholder="+263… test number" value={test} onChange={(e) => setTest(e.target.value)} className="w-56" />
          <Button variant="outline" onClick={runTest} data-testid="wa-test-btn">Send test</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function NotificationsCard() {
  const [busy, setBusy] = useState(false);
  async function enable() {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Push notifications enabled on this device");
    } catch (e) { toast.error(e?.message || "Could not enable push"); }
    finally { setBusy(false); }
  }
  async function test() {
    try {
      await api.post("/notifications/test");
      toast.success("Test notification sent");
    } catch { toast.error("Failed to send test"); }
  }
  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><BellRing className="h-4 w-4 text-deepgreen" />Notification channels</CardTitle>
        <CardDescription>In-app is always on. Enable browser push and email to catch reminders when the app is closed.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-3">
        <Button onClick={enable} disabled={busy} className="bg-deepgreen hover:bg-deepgreen/90" data-testid="enable-push-btn">
          <BellRing className="h-4 w-4 mr-1" /> Enable browser push
        </Button>
        <Button variant="outline" onClick={test} data-testid="test-notif-btn"><KeyRound className="h-4 w-4 mr-1" />Send test notification</Button>
      </CardContent>
    </Card>
  );
}

function UserRow({ u, onChanged }) {
  async function toggle() {
    await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
    toast.success("Updated"); onChanged();
  }
  async function changeRole(role) {
    await api.patch(`/users/${u.id}`, { role });
    toast.success("Role updated"); onChanged();
  }
  return (
    <TableRow>
      <TableCell className="font-medium">{u.name}</TableCell>
      <TableCell>{u.email}</TableCell>
      <TableCell>
        <Select value={u.role} onValueChange={changeRole}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
        </Select>
      </TableCell>
      <TableCell><Switch checked={u.is_active} onCheckedChange={toggle} data-testid={`user-active-${u.email}`} /></TableCell>
      <TableCell>{fmtDate(u.created_at)}</TableCell>
      <TableCell className="text-right"><Badge variant="outline">{roleLabel(u.role)}</Badge></TableCell>
    </TableRow>
  );
}

function NewUser({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "guest", password: "" });
  async function submit() {
    if (form.password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1" />New user</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
          <div className="col-span-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="user-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ThresholdCard({ patient, onSaved }) {
  const [form, setForm] = useState({
    bp_systolic_max: patient.bp_systolic_max,
    bp_diastolic_max: patient.bp_diastolic_max,
    blood_sugar_min: patient.blood_sugar_min,
    blood_sugar_max: patient.blood_sugar_max,
  });
  async function save() {
    try {
      await api.patch(`/health/patients/${patient.id}`, {
        bp_systolic_max: Number(form.bp_systolic_max),
        bp_diastolic_max: Number(form.bp_diastolic_max),
        blood_sugar_min: Number(form.blood_sugar_min),
        blood_sugar_max: Number(form.blood_sugar_max),
      });
      toast.success("Thresholds saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Card className="rounded-2xl">
      <CardHeader><CardTitle>Health thresholds — {patient.name}</CardTitle><CardDescription>Alerts trigger when readings fall outside these bounds. Home360 never diagnoses.</CardDescription></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><Label>BP systolic max</Label><Input type="number" value={form.bp_systolic_max} onChange={(e) => setForm({ ...form, bp_systolic_max: e.target.value })} /></div>
          <div><Label>BP diastolic max</Label><Input type="number" value={form.bp_diastolic_max} onChange={(e) => setForm({ ...form, bp_diastolic_max: e.target.value })} /></div>
          <div><Label>Sugar min</Label><Input type="number" step="0.1" value={form.blood_sugar_min} onChange={(e) => setForm({ ...form, blood_sugar_min: e.target.value })} /></div>
          <div><Label>Sugar max</Label><Input type="number" step="0.1" value={form.blood_sugar_max} onChange={(e) => setForm({ ...form, blood_sugar_max: e.target.value })} /></div>
        </div>
        <div className="mt-4"><Button onClick={save} className="bg-deepgreen hover:bg-deepgreen/90" data-testid="save-thresholds">Save thresholds</Button></div>
      </CardContent>
    </Card>
  );
}
